import { create } from "zustand";
import {
	CurrentInteraction,
	CurrentInteractionActions,
	CurrentInteractionPreview,
	CurrentInteractionState,
	NewInteraction,
} from "./types";

export const useCurrentInteractionStore = create<
	CurrentInteractionState & CurrentInteractionActions
>((set) => ({
	active: undefined,
	new: undefined,
	preview: undefined,
	showNew: (data: NewInteraction) =>
		set((state: CurrentInteractionState) => {
			return { active: state.active, new: data };
		}),
	showActive: (data: CurrentInteraction, hidePreview: boolean) =>
		set((state: CurrentInteractionState) => {
			// Preserve the client-only wrapUpStopped flag across rebuilds of the SAME interaction.
			// endWrapUp sets it by in-place mutation on the current active object (wrapUp.tsx), but the
			// fresh object updateCurrentInteraction builds from the server interactions list does not
			// carry it. Without this, any interactions-list change during the wrap-up linger window
			// (before the server's WrapUpStopped removes the interaction) would replace active with a
			// flag-less object and resurface the just-closed conversation / CRM picker.
			const nextActive: CurrentInteraction =
				state.active?.id === data.id && state.active?.wrapUpStopped
					? { ...data, wrapUpStopped: true }
					: data;

			// Keep the active reference stable when the incoming data is content-identical.
			// updateCurrentInteraction rebuilds a fresh object on every interactions-list change (e.g. a
			// BACKGROUND interaction auto-closing due to inactivity); replacing the reference would re-render
			// every subscriber and recompute WorkspacePanel's component memo — visibly refreshing the open
			// workspace even though the viewed interaction didn't change. Returning the same state object makes
			// zustand skip notifying subscribers entirely. (This JSON comparison is a best-effort optimization,
			// not an exact equality check: active is also set via spreads from a few other call sites whose object
			// shape / key order may differ, and JSON.stringify is order-sensitive. A mismatch simply misses the
			// optimization and falls through to a normal update — never an incorrect result.)
			const unchanged =
				state.active !== undefined &&
				JSON.stringify(state.active) === JSON.stringify(nextActive);

			if (unchanged) {
				return hidePreview && state.preview !== undefined
					? { preview: undefined }
					: state;
			}

			if (hidePreview) {
				return { active: nextActive, new: state.new, preview: undefined };
			} else {
				return { active: nextActive, new: state.new };
			}
		}),
	showPreview: (data: CurrentInteractionPreview) =>
		set((state: CurrentInteractionState) => {
			return { active: state.active, new: state.new, preview: data };
		}),
	hideActive: () =>
		set((state: CurrentInteractionState) =>
			!!state.active ? { active: undefined } : state
		),
	hideNew: () =>
		set((state: CurrentInteractionState) =>
			state.new !== undefined ? { new: undefined } : state
		),
	hidePreview: () =>
		set((state: CurrentInteractionState) =>
			state.preview != undefined ? { preview: undefined } : state
		),
}));
